import { useState, useEffect, useCallback } from 'react';
import { getAdminWithdrawals, approveWithdrawal, rejectWithdrawal } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const TABS = ['all', 'pending', 'processing', 'successful', 'rejected', 'cancelled'];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const statusStyle = {
  pending:    { color: '#ffaa44', bg: 'rgba(255,170,68,0.12)', border: 'rgba(255,170,68,0.3)' },
  processing: { color: '#66aaff', bg: 'rgba(102,170,255,0.12)', border: 'rgba(102,170,255,0.3)' },
  cancelled:  { color: '#888',    bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
  rejected:   { color: '#ff6666', bg: 'rgba(200,50,50,0.12)', border: 'rgba(200,50,50,0.3)' },
  approved:   { color: '#66aaff', bg: 'rgba(102,170,255,0.12)', border: 'rgba(102,170,255,0.3)' },
  successful: { color: '#66ff99', bg: 'rgba(102,255,153,0.12)', border: 'rgba(102,255,153,0.3)' },
};

const AdminWithdrawals = () => {
  const [tab, setTab] = useState('pending');
  const [withdrawals, setWithdrawals] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [destination, setDestination] = useState('all');
  const [search, setSearch] = useState('');
  const [successfulTotal, setSuccessfulTotal] = useState(null);

  // Reject modal
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    setLoading(true);
    getAdminWithdrawals({ status: tab, destination, search, page, limit: 20 })
      .then((res) => {
        setWithdrawals(res.data.withdrawals);
        setTotal(res.data.total);
        setPages(res.data.pages);
        if (res.data.successfulTotal != null) setSuccessfulTotal(res.data.successfulTotal);
      })
      .finally(() => setLoading(false));
  }, [tab, destination, search, page]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this withdrawal?')) return;
    setActionId(id);
    try { await approveWithdrawal(id); load(); } catch (err) { alert(err.response?.data?.message || 'Failed'); }
    finally { setActionId(null); }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    setRejecting(true);
    try { await rejectWithdrawal(rejectModal, { reason: rejectReason }); setRejectModal(null); setRejectReason(''); load(); }
    catch (err) { alert(err.response?.data?.message || 'Failed'); }
    finally { setRejecting(false); }
  };

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Withdrawals</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: successfulTotal != null ? 12 : 20 }}>{total} records</div>
      {successfulTotal != null && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
          <div style={{ background: 'rgba(102,255,153,0.1)', border: '1px solid rgba(102,255,153,0.3)', borderRadius: 10, padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color: '#555', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Successful</span>
            <span style={{ color: '#66ff99', fontSize: 17, fontWeight: 800, fontFamily: 'monospace' }}>${successfulTotal.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button key={t} onClick={() => { setTab(t); setPage(1); }}
            style={{ background: tab === t ? cyan : 'rgba(255,255,255,0.05)', border: 'none', color: tab === t ? '#000' : '#888', borderRadius: 6, padding: '6px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
            {t}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {/* Search */}
        <div>
          <input
            type="text"
            placeholder="Search by user code or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: 'rgba(0,229,255,0.04)',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '10px 14px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>

        {/* Destination Filter */}
        <div style={{ maxWidth: 300 }}>
          <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Withdrawal Type</label>
          <select
            value={destination}
            onChange={(e) => { setDestination(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: '#1a1a1a',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="all" style={{ background: '#1a1a1a', color: '#fff' }}>All Types</option>
            <option value="one_wallet" style={{ background: '#1a1a1a', color: '#ffaa44' }}>OneWallet</option>
            <option value="external" style={{ background: '#1a1a1a', color: '#00e5ff' }}>External Address</option>
          </select>
        </div>
      </div>

      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: isMobile ? 'auto' : 'hidden', overflowY: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '600px' : '100%' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)' }}>
              {['User', 'Amount', 'Fee', 'Receivable', 'Destination', 'Date', 'Status', 'Actions'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
            ) : withdrawals.length === 0 ? (
              <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No withdrawals found.</td></tr>
            ) : withdrawals.map((w) => {
              const ss = statusStyle[w.status] || statusStyle.pending;
              const user = w.user || w.userId; // Support both aggregation and old format
              const destinationType = w.destination === 'one_wallet' ? 'OneWallet' : 'External Address';
              return (
                <tr key={w._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ color: '#fff', fontSize: 13 }}>{user?.name}</div>
                    <div style={{ color: '#555', fontSize: 11 }}>{user?.userCode || user?.email}</div>
                  </td>
                  <td style={{ color: '#fff', fontWeight: 700, fontSize: 13, padding: '11px 14px' }}>{w.amount.toFixed(2)} USDT</td>
                  <td style={{ color: '#ff6666', fontWeight: 600, fontSize: 13, padding: '11px 14px', whiteSpace: 'nowrap' }}>
                    {w.charges.toFixed(2)} <span style={{ color: '#888', fontWeight: 600, fontSize: 11 }}>({w.feePercent ?? 10}%)</span>
                  </td>
                  <td style={{ color: '#66ff99', fontSize: 13, padding: '11px 14px' }}>{w.receivableAmount.toFixed(2)} USDT</td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ color: w.destination === 'one_wallet' ? '#ffaa44' : '#00e5ff', fontSize: 12, fontWeight: 600 }}>{destinationType}</div>
                    {w.externalAddress && (
                      <div style={{ color: '#444', fontSize: 10, fontFamily: 'monospace', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 4, title: w.externalAddress }}>
                        {w.externalAddress}
                      </div>
                    )}
                  </td>
                  <td style={{ color: '#555', fontSize: 11, padding: '11px 14px', whiteSpace: 'nowrap' }}>{fmtIST(w.createdAt)}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ background: ss.bg, border: `1px solid ${ss.border}`, color: ss.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{w.status}</span>
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    {w.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handleApprove(w._id)} disabled={actionId === w._id}
                          style={{ background: 'rgba(102,255,153,0.12)', border: '1px solid rgba(102,255,153,0.3)', color: '#66ff99', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                          {actionId === w._id ? '...' : 'Approve'}
                        </button>
                        <button onClick={() => { setRejectModal(w._id); setRejectReason(''); }}
                          style={{ background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ background: p === page ? cyan : 'rgba(255,255,255,0.06)', border: 'none', color: p === page ? '#000' : '#888', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#111', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 380 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Reject Withdrawal</div>
            <div style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>The amount will be refunded to the user's Earning Wallet.</div>
            <form onSubmit={handleReject}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Reason (Optional)</label>
                <textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter rejection reason..."
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none', resize: 'none', fontFamily: 'inherit' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setRejectModal(null)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={rejecting} style={{ flex: 1, padding: '10px', background: rejecting ? 'rgba(255,255,255,0.06)' : 'rgba(200,50,50,0.2)', border: '1px solid rgba(200,50,50,0.4)', color: rejecting ? '#555' : '#ff6666', borderRadius: 8, fontWeight: 700, cursor: rejecting ? 'not-allowed' : 'pointer' }}>
                  {rejecting ? 'Processing...' : 'Confirm Reject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWithdrawals;
